import { SCALES, ScaleId } from '../data/scales';
import { SHARP_KEYS } from '../data/notes';
import { Tuning } from '../data/tunings';
import { transposeNote, noteToSemitone } from './musicTheory';
import { INTERVAL_BY_SEMITONE, IntervalLabel } from '../data/intervals';
import { colours } from '../theme/colours';

export interface ScaleNote {
  note: string;
  semitones: number;          // semitones above root
  intervalLabel: IntervalLabel | string;
  intervalColor: string;
}

export interface FretboardNote {
  string: number;   // 1 = highest pitch string, increasing toward lowest
  fret: number;
  note: string;
  semitones: number;
  intervalColor: string;
  /** Whether this dot was emitted by the scale walker ('scale') or by the
   *  arpeggio-only overlay walker ('arpeggio-only'). Used by ScaleDiagram
   *  to choose between the in-scale white outline and the arpeggio rings. */
  kind: 'scale' | 'arpeggio-only';
}

/** True if root + scale should be rendered with sharp accidentals. */
function rootUsesSharps(root: string, scaleId: ScaleId): boolean {
  if (SHARP_KEYS.has(root)) return true;
  const minorish: ScaleId[] = ['natural-minor', 'harmonic-minor', 'melodic-minor', 'pentatonic-minor', 'pentatonic-blues'];
  if (minorish.includes(scaleId)) {
    const relMajor = transposeNote(root, 3, true);
    return SHARP_KEYS.has(relMajor);
  }
  return true;
}

export function resolveScaleNotes(rootNote: string, scaleId: ScaleId): ScaleNote[] {
  const scale = SCALES[scaleId];
  if (!scale) return [];

  const sharps = rootUsesSharps(rootNote, scaleId);
  return scale.intervals.map(semi => {
    const note = transposeNote(rootNote, semi, sharps);
    const interval = INTERVAL_BY_SEMITONE[semi];
    return {
      note,
      semitones: semi,
      intervalLabel: interval ? interval.label : String(semi),
      intervalColor: interval ? interval.colour : colours.intervalOther,
    };
  });
}

export function computeFretboardNotes(
  rootNote: string,
  scaleId: ScaleId,
  tuning: Tuning,
  fretRange: [number, number],
): FretboardNote[] {
  const scale = SCALES[scaleId];
  if (!scale) return [];

  const lowFret = Math.max(0, Math.min(24, fretRange[0]));
  const highFret = Math.max(lowFret, Math.min(24, fretRange[1]));

  const rootSemi = noteToSemitone(rootNote);
  const inScale = new Set(scale.intervals);

  const sharps = rootUsesSharps(rootNote, scaleId);

  const out: FretboardNote[] = [];
  // tuning.midi is ordered low-pitch-string-first. For a 6-string standard tuning:
  // midi[0] = low E (string 6); midi[5] = high e (string 1).
  const stringCount = tuning.midi.length;
  for (let stringIndex = 0; stringIndex < stringCount; stringIndex++) {
    const stringSemi = ((tuning.midi[stringIndex] % 12) + 12) % 12;
    const stringNumber = stringCount - stringIndex;

    for (let fret = lowFret; fret <= highFret; fret++) {
      const noteSemi = (stringSemi + fret) % 12;
      const offsetFromRoot = (noteSemi - rootSemi + 12) % 12;
      if (inScale.has(offsetFromRoot)) {
        out.push({
          string: stringNumber,
          fret,
          note: transposeNote(rootNote, offsetFromRoot, sharps),
          semitones: offsetFromRoot,
          intervalColor: INTERVAL_BY_SEMITONE[offsetFromRoot]?.colour ?? colours.intervalOther,
          kind: 'scale',
        });
      }
    }
  }
  return out;
}
